import React, { useState, useRef, useCallback, useMemo, useEffect } from 'react';
import {
  CheckCircle, XCircle, AlertTriangle, Eye, Shield, ShieldAlert,
  Zap, Lock, TrendingUp, TrendingDown, ArrowRight, Hash, Clock,
  UserPlus, Play, ChevronLeft, ChevronRight, ZoomIn, ZoomOut,
} from 'lucide-react';

// ─────────────────────────────────────────────────────────────────────────────
// CONSTANTS & META
// ─────────────────────────────────────────────────────────────────────────────
const EVENT_META = {
  // Enforcement decisions
  ALLOWED:   { color: '#16a34a', bg: '#f0fdf4', icon: CheckCircle,    label: 'Allowed',    shape: 'circle' },
  BLOCKED:   { color: '#dc2626', bg: '#fef2f2', icon: XCircle,        label: 'Blocked',    shape: 'diamond' },
  WARNED:    { color: '#d97706', bg: '#fffbeb', icon: AlertTriangle,   label: 'Warned',     shape: 'triangle' },
  AUDIT:     { color: '#6366f1', bg: '#eef2ff', icon: Eye,            label: 'Audit',      shape: 'circle' },
  REDACTED:  { color: '#8b5cf6', bg: '#f5f3ff', icon: ShieldAlert,    label: 'Redacted',   shape: 'circle' },
  // Autonomy transitions
  UPGRADE:   { color: '#16a34a', bg: '#f0fdf4', icon: TrendingUp,     label: 'Upgrade',    shape: 'star' },
  DOWNGRADE: { color: '#dc2626', bg: '#fef2f2', icon: TrendingDown,   label: 'Downgrade',  shape: 'star' },
  // Lifecycle
  CREATED:   { color: '#0d9488', bg: '#e3f4f1', icon: UserPlus,       label: 'Created',    shape: 'star' },
  RUN:       { color: '#0ea5e9', bg: '#f0f9ff', icon: Play,           label: 'Run',        shape: 'circle' },
};

const AUTONOMY_LABELS = {
  SELF_AUTONOMOUS: 'Self-Autonomous',
  SUPERVISED: 'Supervised',
  RESTRICTED: 'Restricted',
  PROBATION: 'Probation',
};

function getAutonomyRank(level) {
  const ranks = { PROBATION: 0, RESTRICTED: 1, SUPERVISED: 2, SELF_AUTONOMOUS: 3 };
  return ranks[level] ?? -1;
}

// ─────────────────────────────────────────────────────────────────────────────
// UTILITIES
// ─────────────────────────────────────────────────────────────────────────────
// Canonical event time. PolicyAuditEvent + AutonomyTransition both serialize their
// time as `createdAt` (no `timestamp` field), so reading only `e.timestamp` left every
// marker at NaN → unpositioned/invisible. Mirror the vertical TimelineEvent fallback
// (timestamp → at → createdAt) so the scrubber plots ALL events consistently.
const tsOf = (e) => e?.timestamp || e?.at || e?.createdAt || null;

const fmtTime = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit', second: '2-digit' });
};

const fmtDate = (ts) => {
  if (!ts) return '—';
  const d = new Date(ts);
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const fmtFull = (ts) => {
  if (!ts) return '—';
  return `${fmtDate(ts)} ${fmtTime(ts)}`;
};

const truncHash = (h) => h ? h.substring(0, 8) + '…' : '—';

/**
 * Classify a merged timeline item into a visual event type key.
 */
const classifyEvent = (item) => {
  if (item.type === 'TRANSITION') {
    const d = item.data;
    const isUp = getAutonomyRank(d.newLevel) > getAutonomyRank(d.previousLevel);
    return isUp ? 'UPGRADE' : 'DOWNGRADE';
  }
  // Enforcement
  return item.data.decision || 'AUDIT';
};

// ─────────────────────────────────────────────────────────────────────────────
// SVG MARKER SHAPES
// ─────────────────────────────────────────────────────────────────────────────
const Marker = ({ x, y, type, size = 6, active, onClick }) => {
  const meta = EVENT_META[type] || EVENT_META.AUDIT;
  const s = active ? size * 1.6 : size;
  const fill = meta.color;
  const strokeW = active ? 2.5 : 1.5;
  const stroke = active ? '#1c1917' : '#fff';

  const common = {
    style: { cursor: 'pointer', transition: 'all 0.15s ease', filter: active ? 'drop-shadow(0 0 4px rgba(0,0,0,0.25))' : 'none' },
    onClick,
  };

  if (meta.shape === 'diamond') {
    return (
      <g transform={`translate(${x},${y}) rotate(45)`} {...common}>
        <rect x={-s} y={-s} width={s * 2} height={s * 2} rx={1.5} fill={fill} stroke={stroke} strokeWidth={strokeW} />
      </g>
    );
  }
  if (meta.shape === 'triangle') {
    const pts = `${x},${y - s * 1.2} ${x - s * 1.1},${y + s * 0.7} ${x + s * 1.1},${y + s * 0.7}`;
    return <polygon points={pts} fill={fill} stroke={stroke} strokeWidth={strokeW} {...common} />;
  }
  if (meta.shape === 'star') {
    // Simple 4-point star
    const inner = s * 0.45;
    const pts = [];
    for (let i = 0; i < 8; i++) {
      const r = i % 2 === 0 ? s * 1.2 : inner;
      const angle = (Math.PI / 4) * i - Math.PI / 2;
      pts.push(`${x + r * Math.cos(angle)},${y + r * Math.sin(angle)}`);
    }
    return <polygon points={pts.join(' ')} fill={fill} stroke={stroke} strokeWidth={strokeW} {...common} />;
  }
  // Default: circle
  return <circle cx={x} cy={y} r={s} fill={fill} stroke={stroke} strokeWidth={strokeW} {...common} />;
};

// ─────────────────────────────────────────────────────────────────────────────
// LANE LABELS
// ─────────────────────────────────────────────────────────────────────────────
const LANES = [
  { key: 'BLOCKED',    label: 'Blocked',    y: 0 },
  { key: 'WARNED',     label: 'Warned',     y: 1 },
  { key: 'TRANSITION', label: 'Transitions', y: 2 },
  { key: 'ALLOWED',    label: 'Allowed',    y: 3 },
];

const laneForItem = (item) => {
  if (item.type === 'TRANSITION') return 2;
  const d = item.data.decision;
  if (d === 'BLOCKED') return 0;
  if (d === 'WARNED') return 1;
  return 3; // ALLOWED, AUDIT, REDACTED
};

// ─────────────────────────────────────────────────────────────────────────────
// PREVIEW PANEL
// ─────────────────────────────────────────────────────────────────────────────
const PreviewPanel = ({ item, agentName }) => {
  if (!item) {
    return (
      <div style={{
        padding: '32px 24px', textAlign: 'center', color: '#a8a29e', fontSize: 13,
        background: '#fafaf9', borderRadius: 12, border: '1px dashed #e7e5e4',
        minHeight: 160, display: 'flex', alignItems: 'center', justifyContent: 'center',
        flexDirection: 'column', gap: 8,
      }}>
        <Clock size={24} color="#d6d3d1" />
        <span>Drag the scrubber along the timeline or click an event to preview details</span>
      </div>
    );
  }

  const eventType = classifyEvent(item);
  const meta = EVENT_META[eventType] || EVENT_META.AUDIT;
  const Icon = meta.icon;
  const d = item.data;
  const isTransition = item.type === 'TRANSITION';

  return (
    <div style={{
      background: '#ffffff', border: `1px solid #e7e5e4`, borderRadius: 12,
      borderTop: `3px solid ${meta.color}`, overflow: 'hidden',
      boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      animation: 'fadeIn 0.2s ease',
    }}>
      {/* Header bar */}
      <div style={{
        padding: '14px 20px', display: 'flex', alignItems: 'center', gap: 12,
        background: meta.bg, borderBottom: `1px solid ${meta.color}20`,
      }}>
        <div style={{
          width: 36, height: 36, borderRadius: 10, display: 'flex',
          alignItems: 'center', justifyContent: 'center',
          background: meta.color + '18',
        }}>
          <Icon size={18} color={meta.color} />
        </div>
        <div style={{ flex: 1 }}>
          <div style={{ fontSize: 15, fontWeight: 700, color: '#1c1917' }}>
            {isTransition ? 'Autonomy Transition' : (d.eventType || 'Policy Enforcement')}
          </div>
          <div style={{ fontSize: 12, color: '#78716c' }}>
            {fmtFull(d.createdAt)} · {agentName || 'Agent'}
          </div>
        </div>
        <div style={{
          padding: '4px 14px', borderRadius: 20, fontSize: 12, fontWeight: 700,
          color: meta.color, background: meta.bg, border: `1px solid ${meta.color}30`,
        }}>
          {meta.label}
        </div>
      </div>

      {/* Body */}
      <div style={{ padding: '16px 20px' }}>
        {isTransition ? (
          <TransitionPreview data={d} />
        ) : (
          <EnforcementPreview data={d} />
        )}
      </div>
    </div>
  );
};

const TransitionPreview = ({ data }) => {
  const fromLabel = AUTONOMY_LABELS[data.previousLevel] || data.previousLevel;
  const toLabel = AUTONOMY_LABELS[data.newLevel] || data.newLevel;
  const isUp = getAutonomyRank(data.newLevel) > getAutonomyRank(data.previousLevel);
  const dirColor = isUp ? '#16a34a' : '#dc2626';

  return (
    <div>
      {/* Transition flow */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16,
        padding: '12px 16px', background: '#fafaf9', borderRadius: 10,
      }}>
        <div style={{ padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 600, background: '#e7e5e4', color: '#57534e' }}>
          {fromLabel}
        </div>
        <ArrowRight size={18} color={dirColor} />
        <div style={{ padding: '4px 12px', borderRadius: 8, fontSize: 13, fontWeight: 700, background: dirColor + '15', color: dirColor }}>
          {toLabel}
        </div>
        {data.scoreAtTransition != null && (
          <div style={{ marginLeft: 'auto', fontSize: 22, fontWeight: 800, color: dirColor }}>
            {Math.round(Number(data.scoreAtTransition))}
          </div>
        )}
      </div>

      {/* Detail grid */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 20px' }}>
        <Field label="Trigger" value={data.triggerType || '—'} />
        <Field label="Reason" value={data.triggerReason || data.reason || '—'} />
        <Field label="Decided By" value={data.decidedBy || 'System'} />
        {data.violationSummary && <Field label="Violation" value={data.violationSummary} span={2} />}
        <Field label="Hash" value={truncHash(data.transitionHash)} mono />
      </div>
    </div>
  );
};

const EnforcementPreview = ({ data }) => (
  <div>
    {/* Key facts row */}
    <div style={{
      display: 'flex', gap: 16, marginBottom: 16, flexWrap: 'wrap',
    }}>
      {data.skillName && (
        <Chip label="Skill" value={data.skillName} />
      )}
      {data.evaluationTimeMs != null && (
        <Chip label="Eval Time" value={`${data.evaluationTimeMs}ms`} />
      )}
      {data.ruleId && (
        <Chip label="Rule" value={truncHash(data.ruleId)} mono />
      )}
      {data.ruleVersion != null && (
        <Chip label="Version" value={`v${data.ruleVersion}`} />
      )}
    </div>

    {/* Detail grid */}
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '10px 20px' }}>
      <Field label="Event Type" value={data.eventType || '—'} />
      <Field label="Skill ID" value={data.skillId ? truncHash(data.skillId) : '—'} mono />
      <Field label="Workflow" value={data.workflowExecutionId ? truncHash(data.workflowExecutionId) : '—'} mono />
      <Field label="Hash" value={truncHash(data.eventHash)} mono />
    </div>

    {/* Details JSON (collapsed) */}
    {data.details && Object.keys(data.details).length > 0 && (
      <details style={{ marginTop: 12 }}>
        <summary style={{ fontSize: 11, fontWeight: 600, color: '#78716c', cursor: 'pointer', userSelect: 'none' }}>
          Execution Details
        </summary>
        <pre style={{
          background: '#1c1917', color: '#e7e5e4', padding: 12, borderRadius: 8,
          fontSize: 10, lineHeight: 1.5, overflow: 'auto', maxHeight: 140,
          fontFamily: "'JetBrains Mono', monospace", marginTop: 6,
        }}>
          {JSON.stringify(data.details, null, 2)}
        </pre>
      </details>
    )}
  </div>
);

const Field = ({ label, value, mono, span }) => (
  <div style={span ? { gridColumn: `span ${span}` } : {}}>
    <div style={{ fontSize: 10, fontWeight: 600, color: '#a8a29e', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 2 }}>
      {label}
    </div>
    <div style={{
      fontSize: 12, color: '#1c1917', fontWeight: 500, wordBreak: 'break-all',
      fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
    }}>
      {value}
    </div>
  </div>
);

const Chip = ({ label, value, mono }) => (
  <div style={{
    display: 'flex', alignItems: 'center', gap: 6, padding: '5px 12px',
    background: '#f5f5f4', borderRadius: 8, fontSize: 12,
  }}>
    <span style={{ fontWeight: 600, color: '#78716c' }}>{label}:</span>
    <span style={{
      fontWeight: 500, color: '#1c1917',
      fontFamily: mono ? "'JetBrains Mono', monospace" : 'inherit',
    }}>{value}</span>
  </div>
);


// ─────────────────────────────────────────────────────────────────────────────
// MAIN SCRUBBER COMPONENT
// ─────────────────────────────────────────────────────────────────────────────
const TimelineScrubber = ({ events, agentName, agentCreatedAt }) => {
  const containerRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(null);
  const [isDragging, setIsDragging] = useState(false);
  const [scrubX, setScrubX] = useState(null); // pixel position during drag
  const [zoomLevel, setZoomLevel] = useState(1);

  // ── Layout constants ──
  const PADDING_LEFT = 100;
  const PADDING_RIGHT = 40;
  const LANE_HEIGHT = 36;
  const LANE_COUNT = LANES.length;
  const SVG_HEIGHT = 24 + (LANE_COUNT * LANE_HEIGHT) + 40; // top label + lanes + axis
  const BASE_WIDTH = 800;
  const svgWidth = Math.max(BASE_WIDTH, BASE_WIDTH * zoomLevel);
  const trackLeft = PADDING_LEFT;
  const trackRight = svgWidth - PADDING_RIGHT;
  const trackWidth = trackRight - trackLeft;

  // ── Time range ──
  const timeExtent = useMemo(() => {
    if (!events || events.length === 0) return { min: Date.now() - 86400000, max: Date.now() };
    // Use the canonical time field and DROP any unparseable values — a single NaN here
    // would poison Math.min/Math.max and blank the whole axis + every marker.
    const times = events.map((e) => new Date(tsOf(e)).getTime()).filter(Number.isFinite);
    if (agentCreatedAt && Number.isFinite(new Date(agentCreatedAt).getTime())) {
      times.push(new Date(agentCreatedAt).getTime());
    }
    if (times.length === 0) return { min: Date.now() - 86400000, max: Date.now() };
    const min = Math.min(...times);
    const max = Math.max(...times);
    // Add 5% padding on each side
    const pad = Math.max((max - min) * 0.05, 60000); // at least 1 minute
    return { min: min - pad, max: max + pad };
  }, [events, agentCreatedAt]);

  const timeToX = useCallback((ts) => {
    const t = new Date(ts).getTime();
    const pct = (t - timeExtent.min) / (timeExtent.max - timeExtent.min);
    return trackLeft + pct * trackWidth;
  }, [timeExtent, trackLeft, trackWidth]);

  const xToTime = useCallback((x) => {
    const pct = (x - trackLeft) / trackWidth;
    return timeExtent.min + pct * (timeExtent.max - timeExtent.min);
  }, [timeExtent, trackLeft, trackWidth]);

  // ── Positioned events ──
  const positionedEvents = useMemo(() => {
    if (!events || events.length === 0) return [];
    const mapped = events.map((ev, idx) => ({
      ...ev,
      idx,
      x: timeToX(tsOf(ev)),
      lane: laneForItem(ev),
      eventType: classifyEvent(ev),
    }));

    // Dodge overlapping markers WITHIN each lane so a temporal burst (e.g. a 200-call
    // stress run landing in the same second) fans into a visible run instead of
    // collapsing onto a single pixel. Forward pass enforces a min gap; backward pass
    // compresses from the right edge so the run never overflows the track (no clipping).
    const counts = {};
    for (const e of mapped) if (Number.isFinite(e.x)) counts[e.lane] = (counts[e.lane] || 0) + 1;
    const densest = Math.max(1, ...Object.values(counts), 1);
    const gap = Math.min(7, trackWidth / (densest + 1)); // fits the densest lane in-track
    const lanes = {};
    for (const e of mapped) {
      if (!Number.isFinite(e.x)) continue;
      (lanes[e.lane] = lanes[e.lane] || []).push(e);
    }
    for (const laneEvents of Object.values(lanes)) {
      laneEvents.sort((a, b) => (a.x - b.x) || (a.idx - b.idx));
      // forward: push right to maintain gap
      for (let i = 1; i < laneEvents.length; i += 1) {
        const minX = laneEvents[i - 1].x + gap;
        if (laneEvents[i].x < minX) laneEvents[i].x = minX;
      }
      // backward: if the run spilled past the right edge, pull it back in
      const lastIdx = laneEvents.length - 1;
      if (laneEvents[lastIdx].x > trackRight) {
        laneEvents[lastIdx].x = trackRight;
        for (let i = lastIdx - 1; i >= 0; i -= 1) {
          const maxX = laneEvents[i + 1].x - gap;
          if (laneEvents[i].x > maxX) laneEvents[i].x = maxX;
          else break;
        }
      }
      // clamp left edge
      for (const e of laneEvents) if (e.x < trackLeft) e.x = trackLeft;
    }
    return mapped;
  }, [events, timeToX, trackLeft, trackRight, trackWidth]);

  // ── Find nearest event to an X position ──
  const findNearest = useCallback((px) => {
    if (positionedEvents.length === 0) return null;
    let closest = null;
    let minDist = Infinity;
    for (const ev of positionedEvents) {
      const dist = Math.abs(ev.x - px);
      if (dist < minDist) {
        minDist = dist;
        closest = ev;
      }
    }
    return closest;
  }, [positionedEvents]);

  // ── Mouse interaction ──
  const getSvgX = useCallback((clientX) => {
    if (!containerRef.current) return 0;
    const rect = containerRef.current.getBoundingClientRect();
    const scrollLeft = containerRef.current.scrollLeft;
    return clientX - rect.left + scrollLeft;
  }, []);

  const handleMouseDown = useCallback((e) => {
    if (e.button !== 0) return;
    setIsDragging(true);
    const x = getSvgX(e.clientX);
    setScrubX(x);
    const nearest = findNearest(x);
    if (nearest) setActiveIndex(nearest.idx);
  }, [getSvgX, findNearest]);

  const handleMouseMove = useCallback((e) => {
    if (!isDragging) return;
    const x = getSvgX(e.clientX);
    setScrubX(x);
    const nearest = findNearest(x);
    if (nearest) setActiveIndex(nearest.idx);
  }, [isDragging, getSvgX, findNearest]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Attach global mousemove/mouseup during drag
  useEffect(() => {
    if (!isDragging) return;
    const onMove = (e) => handleMouseMove(e);
    const onUp = () => handleMouseUp();
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, [isDragging, handleMouseMove, handleMouseUp]);

  // ── Click on event marker ──
  const handleMarkerClick = (idx) => {
    setActiveIndex(idx);
    const ev = positionedEvents.find((e) => e.idx === idx);
    if (ev) setScrubX(ev.x);
  };

  // ── Navigate between events ──
  const goToEvent = (direction) => {
    if (positionedEvents.length === 0) return;
    let nextIdx;
    if (activeIndex === null) {
      nextIdx = direction > 0 ? 0 : positionedEvents.length - 1;
    } else {
      nextIdx = activeIndex + direction;
      if (nextIdx < 0) nextIdx = 0;
      if (nextIdx >= events.length) nextIdx = events.length - 1;
    }
    setActiveIndex(nextIdx);
    const ev = positionedEvents.find((e) => e.idx === nextIdx);
    if (ev) setScrubX(ev.x);
  };

  // ── Zoom ──
  const handleZoom = (delta) => {
    setZoomLevel((z) => Math.max(0.5, Math.min(5, z + delta)));
  };

  // ── Time axis ticks ──
  const axisTicks = useMemo(() => {
    const range = timeExtent.max - timeExtent.min;
    // Determine good tick interval
    const targetTicks = Math.floor(trackWidth / 120); // ~120px per tick
    const rawInterval = range / Math.max(targetTicks, 1);
    // Snap to nice intervals
    const niceIntervals = [60000, 300000, 600000, 1800000, 3600000, 7200000, 21600000, 43200000, 86400000, 604800000];
    let interval = niceIntervals.find((n) => n >= rawInterval) || rawInterval;

    const ticks = [];
    const start = Math.ceil(timeExtent.min / interval) * interval;
    for (let t = start; t <= timeExtent.max; t += interval) {
      ticks.push({
        time: t,
        x: timeToX(t),
        label: interval >= 86400000 ? fmtDate(t) : fmtTime(t),
      });
    }
    return ticks;
  }, [timeExtent, trackWidth, timeToX]);

  // ── Scrubber position ──
  const scrubberX = scrubX ?? (activeIndex !== null ? positionedEvents.find((e) => e.idx === activeIndex)?.x : null);
  const activeItem = activeIndex !== null ? events[activeIndex] : null;

  // ── No events ──
  if (!events || events.length === 0) {
    return (
      <div style={{
        padding: '32px 24px', textAlign: 'center', color: '#a8a29e', fontSize: 13,
        background: '#fafaf9', borderRadius: 12, border: '1px dashed #e7e5e4',
      }}>
        No events to display on the timeline. Run agent workflows to generate trust events.
      </div>
    );
  }

  return (
    <div>
      {/* Controls bar */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10,
        padding: '6px 0',
      }}>
        <button onClick={() => goToEvent(-1)} style={navBtn} title="Previous event">
          <ChevronLeft size={14} />
        </button>
        <button onClick={() => goToEvent(1)} style={navBtn} title="Next event">
          <ChevronRight size={14} />
        </button>

        <div style={{ flex: 1 }} />

        {/* Event count and current position */}
        <span style={{ fontSize: 11, color: '#78716c', fontWeight: 500 }}>
          {activeIndex !== null ? `Event ${activeIndex + 1} of ${events.length}` : `${events.length} events`}
        </span>

        <div style={{ width: 1, height: 16, background: '#e7e5e4', margin: '0 4px' }} />

        <button onClick={() => handleZoom(-0.25)} style={navBtn} title="Zoom out">
          <ZoomOut size={14} />
        </button>
        <span style={{ fontSize: 10, color: '#a8a29e', minWidth: 30, textAlign: 'center' }}>
          {Math.round(zoomLevel * 100)}%
        </span>
        <button onClick={() => handleZoom(0.25)} style={navBtn} title="Zoom in">
          <ZoomIn size={14} />
        </button>

        {/* Legend */}
        <div style={{ display: 'flex', gap: 10, marginLeft: 12 }}>
          {[
            { color: '#dc2626', label: 'Block' },
            { color: '#d97706', label: 'Warn' },
            { color: '#16a34a', label: 'Allow' },
            { color: '#6366f1', label: 'Transition' },
          ].map((l) => (
            <span key={l.label} style={{ display: 'flex', alignItems: 'center', gap: 3, fontSize: 10, color: '#78716c' }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: l.color, display: 'inline-block' }} />
              {l.label}
            </span>
          ))}
        </div>
      </div>

      {/* Timeline SVG container */}
      <div
        ref={containerRef}
        style={{
          overflowX: 'auto', overflowY: 'hidden',
          background: '#ffffff', border: '1px solid #e7e5e4', borderRadius: 12,
          cursor: isDragging ? 'grabbing' : 'grab',
          userSelect: 'none',
          boxShadow: '0 1px 3px rgba(0,0,0,0.04)',
        }}
        onMouseDown={handleMouseDown}
      >
        <svg width={svgWidth} height={SVG_HEIGHT} style={{ display: 'block' }}>
          {/* Lane backgrounds */}
          {LANES.map((lane, i) => (
            <g key={lane.key}>
              <rect
                x={0} y={24 + i * LANE_HEIGHT}
                width={svgWidth} height={LANE_HEIGHT}
                fill={i % 2 === 0 ? '#fafaf9' : '#ffffff'}
              />
              {/* Lane label */}
              <text
                x={8} y={24 + i * LANE_HEIGHT + LANE_HEIGHT / 2 + 4}
                fontSize={10} fontWeight={600} fill="#a8a29e"
                style={{ textTransform: 'uppercase', letterSpacing: '0.04em' }}
              >
                {lane.label}
              </text>
              {/* Lane divider */}
              <line
                x1={trackLeft} y1={24 + (i + 1) * LANE_HEIGHT}
                x2={trackRight} y2={24 + (i + 1) * LANE_HEIGHT}
                stroke="#f5f5f4" strokeWidth={1}
              />
            </g>
          ))}

          {/* Time axis ticks */}
          {axisTicks.map((tick, i) => (
            <g key={i}>
              <line
                x1={tick.x} y1={24} x2={tick.x} y2={24 + LANE_COUNT * LANE_HEIGHT}
                stroke="#f0f0ee" strokeWidth={1} strokeDasharray="2 3"
              />
              <text
                x={tick.x} y={24 + LANE_COUNT * LANE_HEIGHT + 16}
                textAnchor="middle" fontSize={9} fill="#a8a29e"
              >
                {tick.label}
              </text>
            </g>
          ))}

          {/* Connecting line through events (same lane) */}
          {positionedEvents.length > 1 && (
            <polyline
              fill="none" stroke="#e7e5e4" strokeWidth={1}
              points={positionedEvents.map((ev) => `${ev.x},${24 + ev.lane * LANE_HEIGHT + LANE_HEIGHT / 2}`).join(' ')}
            />
          )}

          {/* Event markers */}
          {positionedEvents.map((ev) => (
            <Marker
              key={ev.id}
              x={ev.x}
              y={24 + ev.lane * LANE_HEIGHT + LANE_HEIGHT / 2}
              type={ev.eventType}
              active={ev.idx === activeIndex}
              onClick={() => handleMarkerClick(ev.idx)}
            />
          ))}

          {/* Scrubber line */}
          {scrubberX != null && (
            <g>
              <line
                x1={scrubberX} y1={16} x2={scrubberX} y2={24 + LANE_COUNT * LANE_HEIGHT + 4}
                stroke="#0d9488" strokeWidth={1.5} strokeDasharray="4 2"
                style={{ pointerEvents: 'none' }}
              />
              {/* Scrubber handle */}
              <g transform={`translate(${scrubberX}, 12)`} style={{ pointerEvents: 'none' }}>
                <polygon points="0,-6 -5,2 5,2" fill="#0d9488" />
              </g>
              {/* Time label above scrubber */}
              {scrubberX != null && (
                <g transform={`translate(${scrubberX}, 6)`}>
                  <rect x={-36} y={-8} width={72} height={14} rx={4} fill="#0d9488" />
                  <text x={0} y={3} textAnchor="middle" fontSize={8} fill="#fff" fontWeight={600}>
                    {fmtTime(xToTime(scrubberX))}
                  </text>
                </g>
              )}
            </g>
          )}
        </svg>
      </div>

      {/* Preview panel */}
      <div style={{ marginTop: 16 }}>
        <PreviewPanel item={activeItem} agentName={agentName} />
      </div>

      {/* Animation */}
      <style>{`@keyframes fadeIn { from { opacity: 0; transform: translateY(4px); } to { opacity: 1; transform: translateY(0); } }`}</style>
    </div>
  );
};

const navBtn = {
  width: 28, height: 28, borderRadius: 6, border: '1px solid #e7e5e4',
  background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
  cursor: 'pointer', color: '#57534e', transition: 'background 0.15s ease',
};

export default TimelineScrubber;
